import type { Diet, OrderLineInput } from "@luckymenu/core";

/**
 * Items picked for an order but not sent to the kitchen yet (OP-07). Pure,
 * so both staff apps edit the same way. A draft survives a reload (UX rule
 * 4): `useOrderDraft` keeps it in the platform's storage. Sending takes the
 * lines out of the draft into the outbox, which sends them with one request
 * ID however often it retries.
 */

export type DraftLine = {
	itemId: string;
	variantId: string | null;
	/** In the item's group order. */
	modifierOptionIds: string[];
	/** 1 to 50, as the API allows per line. */
	quantity: number;
	note: string | null;
	// What the screen shows until the server has priced it.
	name: string;
	variantName: string | null;
	diet: Diet;
	modifiers: { id: string; name: string; priceDeltaPaise: number }[];
	unitPricePaise: number;
	/** For one unit. */
	modifiersTotalPaise: number;
};

/**
 * Where a draft goes: an open order, a free table's new order, a new
 * takeaway, or the quick bill's takeaway (P4-T19), which is billed at once.
 */
export type DraftTarget =
	| { kind: "order"; orderId: string }
	| { kind: "table"; tableId: string }
	| { kind: "takeaway" }
	| { kind: "quick" };

export type OrderDraft = {
	lines: DraftLine[];
	/** New orders only: guests at the table. */
	covers: number | null;
	/** New takeaways only. */
	customerName: string | null;
};

export const EMPTY_DRAFT: OrderDraft = { lines: [], covers: null, customerName: null };

export const MAX_LINE_QUANTITY = 50;
/** The API takes at most 100 lines per batch. */
export const MAX_DRAFT_LINES = 100;

export function draftKey(target: DraftTarget): string {
	if (target.kind === "order") return `order:${target.orderId}`;
	if (target.kind === "table") return `table:${target.tableId}`;
	if (target.kind === "quick") return "quick";
	return "takeaway";
}

/** Lines with the same item, size, add-ons and note are one line. */
export function lineKey(
	line: Pick<DraftLine, "itemId" | "variantId" | "modifierOptionIds" | "note">,
): string {
	return [
		line.itemId,
		line.variantId ?? "",
		[...line.modifierOptionIds].sort().join("+"),
		line.note ?? "",
	].join("|");
}

const clampQuantity = (n: number) => Math.max(0, Math.min(MAX_LINE_QUANTITY, Math.floor(n)));

/** Adds `line` (with its quantity), joining an equal line. */
export function addLine(draft: OrderDraft, line: DraftLine): OrderDraft {
	const key = lineKey(line);
	const at = draft.lines.findIndex((l) => lineKey(l) === key);
	if (at === -1) {
		if (draft.lines.length >= MAX_DRAFT_LINES) return draft;
		return {
			...draft,
			lines: [...draft.lines, { ...line, quantity: clampQuantity(line.quantity) }],
		};
	}
	return setQuantity(draft, key, (draft.lines[at]?.quantity ?? 0) + line.quantity);
}

/** Sets a line's quantity; 0 removes it. At most 50 per line. */
export function setQuantity(draft: OrderDraft, key: string, quantity: number): OrderDraft {
	const q = clampQuantity(quantity);
	const lines =
		q === 0
			? draft.lines.filter((l) => lineKey(l) !== key)
			: draft.lines.map((l) => (lineKey(l) === key ? { ...l, quantity: q } : l));
	return { ...draft, lines };
}

export function changeQuantity(draft: OrderDraft, key: string, delta: number): OrderDraft {
	const line = draft.lines.find((l) => lineKey(l) === key);
	return line ? setQuantity(draft, key, line.quantity + delta) : draft;
}

export function removeLine(draft: OrderDraft, key: string): OrderDraft {
	return setQuantity(draft, key, 0);
}

/** Changes a line's note; a line that becomes equal to another joins it. */
export function setLineNote(draft: OrderDraft, key: string, note: string): OrderDraft {
	const at = draft.lines.findIndex((l) => lineKey(l) === key);
	const current = draft.lines[at];
	if (!current) return draft;
	const line = { ...current, note: note.trim().slice(0, 140) || null };
	const newKey = lineKey(line);
	const other = draft.lines.findIndex((l, i) => i !== at && lineKey(l) === newKey);
	if (other === -1) return { ...draft, lines: draft.lines.map((l, i) => (i === at ? line : l)) };
	return {
		...draft,
		lines: draft.lines
			.map((l, i) =>
				i === other ? { ...l, quantity: clampQuantity(l.quantity + line.quantity) } : l,
			)
			.filter((_, i) => i !== at),
	};
}

export function lineTotalPaise(line: DraftLine): number {
	return (line.unitPricePaise + line.modifiersTotalPaise) * line.quantity;
}

/** Units and value before tax, for the Send button and the draft's footer. */
export function draftSummary(draft: OrderDraft): { count: number; totalPaise: number } {
	return {
		count: draft.lines.reduce((n, l) => n + l.quantity, 0),
		totalPaise: draft.lines.reduce((n, l) => n + lineTotalPaise(l), 0),
	};
}

/** What the API takes for `createOrder.batch.lines` and `addBatch.lines`. */
export function draftToLines(lines: readonly DraftLine[]): OrderLineInput[] {
	return lines.map((l) => ({
		itemId: l.itemId,
		variantId: l.variantId,
		modifierOptionIds: l.modifierOptionIds,
		quantity: l.quantity,
		note: l.note,
	}));
}

/** Reads a stored draft, dropping anything that does not look like one. */
export function parseDraft(raw: string | null): OrderDraft | null {
	if (!raw) return null;
	try {
		const value = JSON.parse(raw) as Partial<OrderDraft>;
		if (!Array.isArray(value.lines)) return null;
		const lines = value.lines.filter(
			(l): l is DraftLine =>
				typeof l === "object" &&
				l !== null &&
				typeof l.itemId === "string" &&
				typeof l.quantity === "number" &&
				Array.isArray(l.modifierOptionIds) &&
				Array.isArray(l.modifiers),
		);
		return {
			lines,
			covers: typeof value.covers === "number" ? value.covers : null,
			customerName: typeof value.customerName === "string" ? value.customerName : null,
		};
	} catch {
		return null;
	}
}

export function isEmptyDraft(draft: OrderDraft): boolean {
	return draft.lines.length === 0 && draft.covers === null && draft.customerName === null;
}
